<template>
  <div class="cold-start-site-config">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>站点全局配置</h2>
    </div>

    <!-- 上架日期范围说明 -->
    <div class="tip-banner">
      <span class="tip-banner__icon">📅</span>
      <div>
        <div class="tip-banner__title">上架日期范围说明</div>
        <div class="tip-banner__desc">
          该参数决定哪些商品（按上架时间）可以参与冷启动流量扶持。例如设置「近
          90 天上架」，则仅上架时间在过去 90 天内的商品才会被纳入扶持范围。
        </div>
      </div>
    </div>

    <!-- 站点配置区 -->
    <div class="site-config-card">
      <div class="site-config-card__header">
        <div class="site-config-card__header-left">
          <global-outlined />
          <span>站点配置</span>
        </div>
        <div class="site-config-card__header-right">
          <clock-circle-outlined />
          <span>
            上次保存：{{ state.lastSavedTime }} · {{ state.lastSavedBy }}
          </span>
        </div>
      </div>

      <div class="site-config-card__body">
        <!-- 目标站点 -->
        <div class="site-config-card__row">
          <div class="site-config-card__field">
            <label>目标站点</label>
            <a-select
              v-model:value="state.selectedSiteId"
              style="width: 180px"
              @change="state.onSiteChange()"
            >
              <a-select-option
                v-for="item in state.sites"
                :key="item.id"
                :value="item.id"
              >
                {{ item.code }} {{ item.name }}
              </a-select-option>
            </a-select>
          </div>

          <!-- 上架日期范围配置 -->
          <div class="site-config-card__field-group">
            <label>上架日期范围配置</label>
            <div class="site-config-card__range">
              <div class="site-config-card__range-item">
                <span class="range-label">
                  起始（天前）<span class="required">*</span>
                </span>
                <a-input-number
                  v-model:value="state.siteForm.startDays"
                  :min="1"
                  :max="365"
                  :precision="0"
                  style="width: 100px"
                />
                <span class="range-suffix">天前上架</span>
              </div>
              <span class="range-separator">〉</span>
              <div class="site-config-card__range-item">
                <span class="range-label">结束（天前）</span>
                <span class="range-label-hint">（0 = 今天）</span>
                <a-input-number
                  v-model:value="state.siteForm.endDays"
                  :min="1"
                  :max="365"
                  :precision="0"
                  style="width: 100px"
                />
                <span class="range-suffix">天前上架</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 当前配置提示 -->
        <div class="site-config-card__hint">
          <span>📅</span>
          <span>
            当前配置：仅上架时间在
            <strong>过去 {{ state.siteForm.startDays || 0 }} 天</strong>
            内的商品可参与冷启动流量扶持
          </span>
        </div>

        <!-- 保存按钮 -->
        <div class="site-config-card__actions">
          <a-button type="primary" @click="state.saveSiteConfig()">
            <template #icon><save-outlined /></template>
            保存配置
          </a-button>
          <span class="site-config-card__status">
            <check-circle-outlined style="color: #52c41a" />
            配置已是最新
          </span>
        </div>
      </div>
    </div>

    <!-- 所有站点概览 -->
    <div class="site-overview">
      <h3>所有站点概览</h3>
      <div class="site-overview__grid">
        <div
          v-for="site in state.siteOverviewList"
          :key="site.id"
          class="site-overview__card"
          :class="{
            'site-overview__card--active': site.id === state.selectedSiteId
          }"
          @click="state.selectSiteFromOverview(site.id)"
        >
          <div class="site-overview__card-header">
            <span class="site-overview__card-code">{{ site.code }}</span>
            <a-tag v-if="site.id === state.selectedSiteId" color="blue">
              当前
            </a-tag>
            <global-outlined class="site-overview__card-icon" />
          </div>
          <div class="site-overview__card-name">{{ site.name }}</div>
          <div class="site-overview__card-info">
            📅 近 {{ site.startDays }} 天上架
          </div>
          <div class="site-overview__card-time">
            更新于 {{ site.updatedAt }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toRefs, reactive, provide, onMounted } from 'vue'
import {
  GlobalOutlined,
  ClockCircleOutlined,
  SaveOutlined,
  CheckCircleOutlined
} from '@ant-design/icons-vue'
import useAPI from 'UseApi'
import { message } from 'ant-design-vue'

const { common_querySiteInfo_get_ } = useAPI(null, [3426])

export default {
  name: 'NewProductColdStartSiteConfig',
  components: {
    GlobalOutlined,
    ClockCircleOutlined,
    SaveOutlined,
    CheckCircleOutlined
  },
  setup() {
    const state = reactive({
      sites: [],
      brands: [],
      channels: [],
      selectedSiteId: 1,
      lastSavedTime: '2026-04-20 10:00',
      lastSavedBy: '刘子涵',
      siteForm: {
        startDays: 30,
        endDays: 1
      },
      siteOverviewList: [],
      onSiteChange: () => {
        const site = state.siteOverviewList.find(
          item => item.id === state.selectedSiteId
        )
        if (site) {
          state.siteForm.startDays = site.startDays
          state.siteForm.endDays = site.endDays
        }
      },
      selectSiteFromOverview: id => {
        state.selectedSiteId = id
        state.onSiteChange()
      },
      saveSiteConfig: () => {
        if (!state.siteForm.startDays && state.siteForm.startDays !== 0) {
          message.error('请输入起始天数')
          return
        }
        // TODO: 调用后端接口保存配置
        const site = state.siteOverviewList.find(
          item => item.id === state.selectedSiteId
        )
        if (site) {
          site.startDays = state.siteForm.startDays
          site.endDays = state.siteForm.endDays
          site.updatedAt = new Date().toLocaleString('zh-CN')
        }
        message.success('配置保存成功')
      },
      initSiteOverview: () => {
        const mockData = [
          { startDays: 90, endDays: 0, updatedAt: '2026-04-20 10:00' },
          { startDays: 60, endDays: 0, updatedAt: '2026-04-18 14:30' },
          { startDays: 45, endDays: 0, updatedAt: '2026-04-15 09:00' },
          { startDays: 30, endDays: 0, updatedAt: '2026-04-10 16:00' },
          { startDays: 60, endDays: 0, updatedAt: '2026-04-08 11:00' },
          { startDays: 90, endDays: 0, updatedAt: '2026-04-05 15:00' }
        ]
        state.siteOverviewList = state.sites.map((site, index) => ({
          id: site.id,
          code: site.code || site.name?.substring(0, 2).toUpperCase(),
          name: site.name,
          startDays: mockData[index]?.startDays || 30,
          endDays: mockData[index]?.endDays || 0,
          updatedAt: mockData[index]?.updatedAt || '—'
        }))
        state.onSiteChange()
      }
    })

    onMounted(async () => {
      await common_querySiteInfo_get_({ state })
      state.initSiteOverview()
    })

    provide('initState', state)
    return {
      state,
      ...toRefs(state)
    }
  }
}
</script>

<style lang="scss" scoped>
.cold-start-site-config {
  padding: 20px;
}

.page-header {
  margin-bottom: 16px;

  h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
}

.tip-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: #f0f5ff;
  border: 1px solid #d6e4ff;
  border-radius: 6px;
  margin-bottom: 20px;

  &__icon {
    font-size: 18px;
    flex-shrink: 0;
  }

  &__title {
    font-weight: 500;
    color: #1890ff;
    margin-bottom: 4px;
  }

  &__desc {
    color: #666;
    font-size: 13px;
    line-height: 1.6;
  }
}

.site-config-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 24px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;

    &-left {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
      font-size: 15px;
    }

    &-right {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #999;
      font-size: 13px;
    }
  }

  &__body {
    padding: 24px;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 40px;
    margin-bottom: 20px;
  }

  &__field {
    label {
      display: block;
      font-size: 13px;
      color: #666;
      margin-bottom: 8px;
    }
  }

  &__field-group {
    label {
      display: block;
      font-size: 13px;
      color: #666;
      margin-bottom: 8px;
    }
  }

  &__range {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__range-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__hint {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    margin-bottom: 20px;
    font-size: 13px;
    color: #666;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #52c41a;
    font-size: 13px;
  }
}

.range-label {
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}

.range-label-hint {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

.range-suffix {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

.range-separator {
  color: #999;
  font-size: 16px;
}

.required {
  color: #ff4d4f;
}

.site-overview {
  h3 {
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 16px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  &__card {
    padding: 16px 20px;
    background: #ffffff;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #d6e4ff;
    }

    &--active {
      border-color: #1890ff;
      background: #f0f5ff;
    }

    &-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
    }

    &-code {
      font-weight: 600;
      font-size: 15px;
    }

    &-icon {
      margin-left: auto;
      color: #999;
    }

    &-name {
      color: #666;
      font-size: 13px;
      margin-bottom: 8px;
    }

    &-info {
      font-size: 13px;
      color: #333;
      margin-bottom: 4px;
    }

    &-time {
      font-size: 12px;
      color: #999;
    }
  }
}
</style>
